/**
 * Google Drive integration for Adulting documents.
 *
 * Supports two paths:
 * 1. Import existing Google Drive files into Adulting-managed storage.
 * 2. Store new Adulting documents directly in a user's own Google Drive folder tree.
 */

import { fetchGoogleUserEmail, GOOGLE_SCOPES, isGoogleConfigured, requestGoogleAccessToken } from '@/lib/google-auth';

declare global {
  interface Window {
    gapi: {
      load: (lib: string, cb: () => void) => void;
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const google: any;
}

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  url: string;
  iconUrl?: string;
  sizeBytes?: number;
}

export interface DriveFolderRecord {
  id: string;
  name: string;
  webViewLink?: string;
  parents?: string[];
}

export interface DriveUploadResult {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes?: number;
  webViewLink?: string;
  parents?: string[];
}

export type AdultingDriveFolderKey =
  | 'money'
  | 'home'
  | 'health'
  | 'family'
  | 'pets'
  | 'work'
  | 'education'
  | 'business';

const API_KEY = import.meta.env.VITE_GOOGLE_API_KEY as string | undefined;
const PICKER_APP_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined)?.split('-')[0] ?? '';
const ROOT_FOLDER_NAME = 'Adulting';

const PICKER_SCOPES = [
  GOOGLE_SCOPES.driveReadonly,
  GOOGLE_SCOPES.driveFile,
  GOOGLE_SCOPES.userInfoEmail,
];

const DRIVE_WRITE_SCOPES = [
  GOOGLE_SCOPES.driveFile,
  GOOGLE_SCOPES.driveReadonly,
  GOOGLE_SCOPES.userInfoEmail,
];

const DRIVE_FOLDERS: Array<{ key: AdultingDriveFolderKey; name: string }> = [
  { key: 'money', name: '01 Money' },
  { key: 'home', name: '02 Home' },
  { key: 'health', name: '03 Health' },
  { key: 'family', name: '04 Family' },
  { key: 'work', name: '05 Work' },
  { key: 'education', name: '06 Education' },
  { key: 'business', name: '07 Self employment & business' },
  { key: 'pets', name: '08 Pets' },
];

let gapiLoaded = false;
let driveAccessToken: string | null = null;

function escapeDriveQueryValue(value: string) {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

async function ensureGapi(): Promise<void> {
  if (gapiLoaded) return;
  await loadScript('https://apis.google.com/js/api.js');
  await new Promise<void>((resolve) => window.gapi.load('picker', resolve));
  gapiLoaded = true;
}

async function ensureDriveToken(scopes = DRIVE_WRITE_SCOPES, forceRefresh = false) {
  if (driveAccessToken && !forceRefresh) {
    return driveAccessToken;
  }

  driveAccessToken = await requestGoogleAccessToken(scopes);
  return driveAccessToken;
}

async function authorizedDriveRequest(path: string, init?: RequestInit, scopes = DRIVE_WRITE_SCOPES) {
  const accessToken = await ensureDriveToken(scopes);
  const response = await fetch(`https://www.googleapis.com/drive/v3${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(init?.headers || {}),
    },
  });

  return { accessToken, response };
}

async function ensureDriveFolder(name: string, parentId?: string) {
  const queryParts = [
    "mimeType = 'application/vnd.google-apps.folder'",
    'trashed = false',
    `name = '${escapeDriveQueryValue(name)}'`,
  ];

  if (parentId) {
    queryParts.push(`'${escapeDriveQueryValue(parentId)}' in parents`);
  }

  const { response: searchResponse } = await authorizedDriveRequest(
    `/files?q=${encodeURIComponent(queryParts.join(' and '))}&fields=files(id,name,webViewLink,parents)&spaces=drive`,
  );

  if (!searchResponse.ok) {
    const message = await searchResponse.text();
    throw new Error(`Could not inspect your Google Drive folders: ${message}`);
  }

  const existing = ((await searchResponse.json()).files || []) as DriveFolderRecord[];
  if (existing[0]) {
    return existing[0];
  }

  const { response: createResponse } = await authorizedDriveRequest('/files?fields=id,name,webViewLink,parents', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name,
      mimeType: 'application/vnd.google-apps.folder',
      parents: parentId ? [parentId] : undefined,
    }),
  });

  if (!createResponse.ok) {
    const message = await createResponse.text();
    throw new Error(`Could not create a Google Drive folder: ${message}`);
  }

  return (await createResponse.json()) as DriveFolderRecord;
}

export async function ensureAdultingDriveStructure({ childNames = [] }: { childNames?: string[] }) {
  const accessToken = await ensureDriveToken();
  const userEmail = await fetchGoogleUserEmail(accessToken);
  const rootFolder = await ensureDriveFolder(ROOT_FOLDER_NAME);

  const foldersByKey = {} as Record<AdultingDriveFolderKey, DriveFolderRecord>;
  for (const folder of DRIVE_FOLDERS) {
    foldersByKey[folder.key] = await ensureDriveFolder(folder.name, rootFolder.id);
  }

  const familyGeneralFolder = await ensureDriveFolder('General family', foldersByKey.family.id);
  const childFolders: DriveFolderRecord[] = [];

  const uniqueChildNames = Array.from(
    new Set(childNames.map((name) => name.trim()).filter(Boolean)),
  );

  for (const childName of uniqueChildNames) {
    childFolders.push(await ensureDriveFolder(childName, foldersByKey.family.id));
  }

  return {
    userEmail,
    rootFolder,
    foldersByKey,
    familyGeneralFolder,
    childFolders,
  };
}

export async function uploadFileToGoogleDrive({
  file,
  parentId,
}: {
  file: File;
  parentId: string;
}) {
  const accessToken = await ensureDriveToken();
  const formData = new FormData();

  formData.append(
    'metadata',
    new Blob(
      [
        JSON.stringify({
          name: file.name,
          parents: [parentId],
          description: 'Stored by Adulting',
        }),
      ],
      { type: 'application/json' },
    ),
  );
  formData.append('file', file);

  const response = await fetch(
    'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,mimeType,size,webViewLink,parents',
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Could not upload to Google Drive: ${message}`);
  }

  const data = await response.json();
  return {
    id: data.id as string,
    name: data.name as string,
    mimeType: data.mimeType as string,
    sizeBytes: data.size ? Number(data.size) : undefined,
    webViewLink: data.webViewLink as string | undefined,
    parents: data.parents as string[] | undefined,
  } as DriveUploadResult;
}

export async function deleteGoogleDriveFile(fileId: string) {
  const { response } = await authorizedDriveRequest(`/files/${encodeURIComponent(fileId)}`, {
    method: 'DELETE',
  });

  if (!response.ok && response.status !== 404) {
    const message = await response.text();
    throw new Error(`Could not delete that Google Drive file: ${message}`);
  }
}

export async function fetchGoogleDriveFileViewLink(fileId: string) {
  const { response } = await authorizedDriveRequest(
    `/files/${encodeURIComponent(fileId)}?fields=id,name,webViewLink`,
    undefined,
    PICKER_SCOPES,
  );

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Could not open that Google Drive file: ${message}`);
  }

  const data = await response.json();
  return data.webViewLink as string | undefined;
}

export async function openGoogleDrivePicker(): Promise<DriveFile[]> {
  if (!API_KEY) {
    throw new Error('VITE_GOOGLE_API_KEY is not set. Add it to your .env to enable Google Drive.');
  }

  await Promise.all([ensureGapi(), ensureDriveToken(PICKER_SCOPES)]);

  return new Promise((resolve) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const picker = new (google as any).picker.PickerBuilder()
      .addView(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        new (google as any).picker.DocsView()
          .setIncludeFolders(false)
          .setSelectFolderEnabled(false),
      )
      .setOAuthToken(driveAccessToken!)
      .setDeveloperKey(API_KEY)
      .setAppId(PICKER_APP_ID)
      .setTitle('Choose files to import into Adulting')
      .enableFeature((google as any).picker.Feature.MULTISELECT_ENABLED)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .setCallback((data: any) => {
        if (data.action === (google as any).picker.Action.PICKED) {
          resolve(
            data.docs.map(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (doc: any) =>
                ({
                  id: doc.id,
                  name: doc.name,
                  mimeType: doc.mimeType,
                  url: `https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media`,
                  iconUrl: doc.iconUrl,
                  sizeBytes: doc.sizeBytes,
                }) as DriveFile,
            ),
          );
        } else if (data.action === (google as any).picker.Action.CANCEL) {
          resolve([]);
        }
      })
      .build();

    picker.setVisible(true);
  });
}

export async function downloadDriveFile(file: DriveFile): Promise<Blob> {
  const accessToken = await ensureDriveToken(PICKER_SCOPES);
  const response = await fetch(file.url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to download ${file.name}: ${response.status} ${response.statusText}`);
  }

  return response.blob();
}

export function isGoogleDriveConfigured(): boolean {
  return isGoogleConfigured();
}

export async function listGoogleDriveImportableFiles(query = ''): Promise<DriveFile[]> {
  const clauses = ["trashed = false", "mimeType != 'application/vnd.google-apps.folder'"];
  if (query.trim()) {
    clauses.push(`name contains '${escapeDriveQueryValue(query.trim())}'`);
  }

  const { response } = await authorizedDriveRequest(
    `/files?q=${encodeURIComponent(clauses.join(' and '))}&fields=files(id,name,mimeType,iconLink,size)&orderBy=modifiedTime desc&pageSize=50&spaces=drive`,
    undefined,
    PICKER_SCOPES,
  );

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Could not load your Google Drive files: ${message}`);
  }

  const data = await response.json();
  return ((data.files || []) as Array<Record<string, unknown>>)
    .filter((file) => {
      const mimeType = typeof file.mimeType === 'string' ? file.mimeType : '';
      return !mimeType.startsWith('application/vnd.google-apps');
    })
    .map(
      (file) =>
        ({
          id: file.id as string,
          name: file.name as string,
          mimeType: (file.mimeType as string) || 'application/octet-stream',
          url: `https://www.googleapis.com/drive/v3/files/${file.id}?alt=media`,
          iconUrl: file.iconLink as string | undefined,
          sizeBytes: file.size ? Number(file.size) : undefined,
        }) satisfies DriveFile,
    );
}
